import { hydrateCollectionsFilterUI } from '../utils/collection-filter-hydration';
import { linkDeviceSelectDuplicate } from '../modules/device-select-duplicate';
import { loadLists, loadViewState, markListRestored, restoreScroll, saveLists, saveViewState, type ListToSave } from '../utils/collection-state-store';
import { ParentCollection, type IdsPage } from '../modules/parent-collection';

// Configuration
const cfSelectors = {
  mainContent: '#MainContent',
  sections: '[data-product-section]',
  collectionHandle: '[data-collection-handle]',
  loadMoreButton: '[data-load-more]',
  filters: {
    toggleDevices: '[data-toggle-device]',
    toggleProductType: '[data-toggle-product-type]',
    devicesContainer: '.pt_filters_container',
    productTypeContainer: '.pt_filter',
    phoneDevice: '#phone-device'
  }
};

interface CollectionState {
  currentMarket: string;
  collectionHandle: string;
  currentLanguage: string;
  shopUrl: string;
  accessToken: string;
  activeColorFilter: string[];
  activeDesignFilter: string[];
  activeCollectionFilter?: string[];
  activeForHimFilter: string[];
}

const cfState: CollectionState = {
  currentMarket: document.querySelector('body[data-country]') instanceof HTMLElement
    ? (document.querySelector('body[data-country]') as HTMLElement).dataset.country?.toUpperCase() === 'CA' ? 'CA' : 'US'
    : 'US',
  collectionHandle: (document.querySelector('[data-collection-handle]') as HTMLElement).dataset.collectionHandle!,
  currentLanguage: document.documentElement.lang.toUpperCase(),
  shopUrl: "https://kaseme-dev-page.myshopify.com",
  accessToken: "a815422836370e8d05bd0c6a813dbaa5",
  activeColorFilter: [],
  activeDesignFilter: [],
  activeForHimFilter: []
};

// Persisted collection state (see utils/collection-state-store.ts)
const LEGACY_STATE_KEY_PREFIX = 'collectionState:';

// One snapshot per list variant: the cards differ by language and market, and the device
// filter (favPhone cookie) changes which products are listed.
function getCollectionStateKey(): string {
  const favPhone = CookieManager.getCookie('favPhone') || '';
  return [cfState.collectionHandle, cfState.currentLanguage, cfState.currentMarket, favPhone].join(':');
}

// Identifies a filter checkbox across page loads
function getFilterId(input: HTMLInputElement): string {
  return `${input.name}=${input.value}`;
}

function getCheckedFilterIds(): string[] {
  return Array.from(document.querySelectorAll<HTMLInputElement>('.filters-container input[type="checkbox"]:checked')).map(getFilterId);
}

// Called after every list change (initial load, load more, filter reset) rather than on the
// product click: an IndexedDB write started while the page unloads is not guaranteed to land.
// resetSection names the list that was just rebuilt from scratch, if any.
function saveCollectionState(resetSection?: string): void {
  const lists: ListToSave[] = [];

  document.querySelectorAll(cfSelectors.sections).forEach((sectionEl) => {
    const el = sectionEl as HTMLElement;
    const ul = el.querySelector('ul') as HTMLElement | null;
    if (!ul) return;
    // A list whose first fetch hasn't landed (or failed) still holds the card-loader skeletons
    if (ul.dataset.restored !== 'true' && ul.dataset.loaded !== 'true') return;

    const section = el.dataset.section!;
    lists.push({
      ul,
      isReset: section === resetSection,
      state: {
        section,
        cursor: el.dataset.cursor || '',
        hasNextPage: el.dataset.hasNextPage || '',
        timeReload: ul.dataset.timeReload || '1',
        listSource: el.dataset.listSource || ''
      }
    });
  });

  saveLists(getCollectionStateKey(), lists, getCheckedFilterIds());
}

async function restoreCollectionState(): Promise<boolean> {
  try {
    // Free the quota held by the pre-IndexedDB format
    sessionStorage.removeItem(LEGACY_STATE_KEY_PREFIX + cfState.collectionHandle);
  } catch (e) {
    // Storage blocked: nothing to clean up
  }

  const key = getCollectionStateKey();
  const snapshot = await loadLists(key);
  if (!snapshot || snapshot.lists.length === 0) return false;

  snapshot.lists.forEach((savedSection) => {
    const sectionEl = document.querySelector(`[data-section="${savedSection.section}"]`) as HTMLElement | null;
    if (!sectionEl) return;
    const ul = sectionEl.querySelector('ul') as HTMLElement | null;
    if (!ul) return;

    ul.innerHTML = savedSection.html;
    ul.dataset.timeReload = savedSection.timeReload || '1';
    ul.dataset.restored = 'true';
    markListRestored(ul, key, savedSection.chunkCount);

    if (savedSection.cursor) {
      sectionEl.dataset.cursor = savedSection.cursor;
    }
    if (savedSection.listSource) {
      sectionEl.dataset.listSource = savedSection.listSource;
    }
    if (savedSection.hasNextPage !== undefined && savedSection.hasNextPage !== '') {
      sectionEl.dataset.hasNextPage = savedSection.hasNextPage;
    }

    const loadingState = sectionEl.querySelector('.load-more-row');
    if (loadingState) {
      if (sectionEl.dataset.hasNextPage && sectionEl.dataset.hasNextPage !== 'false') {
        loadingState.classList.remove('--hidden');
        loadingState.querySelector('.loader')?.classList.add('--hidden');
        loadingState.querySelector('button')?.classList.remove('--hidden');
      } else {
        loadingState.classList.add('--hidden');
      }
    }
  });

  // Restored cards are fresh nodes: rebind the wishlist buttons like after any re-render
  reinitWishlistWidget();

  // The restored lists were fetched with these filters: put them back in the UI and state before
  // the managers are built, so a list that wasn't saved yet fetches with the same filters
  const filtersContainer = document.querySelector('.filters-container');
  if (filtersContainer) {
    filtersContainer.querySelectorAll<HTMLInputElement>('input[type="checkbox"]').forEach((input) => {
      input.checked = snapshot.filters.includes(getFilterId(input));
    });
    syncFilterStateFromCheckboxes(filtersContainer);
  }

  return true;
}

// Runs once the managers exist: re-opening the product type comes before the scroll, since
// "Load More" hides the other types and the saved position belongs to that shorter page
function restoreViewState(filterManager: FilterManager): void {
  const view = loadViewState(getCollectionStateKey());
  if (!view) return;

  const { activeSection } = view;
  if (activeSection && activeSection !== 'all' && document.querySelector(`[data-section="${activeSection}"]`)) {
    filterManager.setActiveSection(activeSection);
  }

  restoreScroll(view, cfSelectors.sections);
}

// Utilities
class CookieManager {
  static setCookie(name: string, value: string, days = 14): void {
    const maxAge = days * 24 * 60 * 60;
    document.cookie = `${name}=${value}; path=/; max-age=${maxAge};`;
  }

  static getCookie(name: string): string | undefined {
    return document.cookie
      .split('; ')
      .find(row => row.startsWith(name + '='))
      ?.split('=')[1];
  }
}

// Browser detection
function isSafari(version: number): boolean {
  const userAgent = navigator.userAgent;
  return userAgent.indexOf("Safari") !== -1 &&
         userAgent.indexOf("Chrome") === -1 &&
         parseFloat(userAgent.split("Version/")[1]) <= version;
}

// Helpers
function arraysEqual(a: string[], b: string[]): boolean {
  if (a === b) return true;
  if (!a || !b || a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

function debounce<T extends unknown[]>(fn: (...args: T) => void, wait: number): (...args: T) => void {
  let timeout: ReturnType<typeof setTimeout> | undefined;
  return (...args: T) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn.apply(null, args), wait);
  };
}

// --- Filter fragment cache ---
// All 12 section query builders share the same filter cfState. Computing fragments
// once per reload cycle instead of 36 times (12 sections × 3 fragments) saves
// redundant array iterations whenever resetSections() fires.
const _debugCollectionsFilterStorage =
  typeof localStorage !== 'undefined' && localStorage.getItem('kmDebugCollectionsFilter') === 'true';

interface FilterFragments {
  color: string;
  design: string;
  collection: string;
  forHim: string;
}
let _filterFragmentCache: FilterFragments | null = null;

function getOrBuildFragments(): FilterFragments {
  if (_filterFragmentCache) return _filterFragmentCache;

  const color = cfState.activeColorFilter.length === 0 ? '' :
    cfState.activeColorFilter.map(v => `{ productMetafield: { namespace: "custom", key: "colors", value: "${v}" } }`).join(',');

  const design = cfState.activeDesignFilter.length === 0 ? '' :
    cfState.activeDesignFilter.map(v => `{ productMetafield: { namespace: "custom", key: "designs", value: "${v}" } }`).join(',');

  let collection = '';
  if (cfState.activeCollectionFilter?.length) {
    collection = cfState.activeCollectionFilter.map(v => `{ productMetafield: { namespace: "custom", key: "collections", value: "${v}" } }`).join(',');
  }

  const forHim = cfState.activeForHimFilter.length === 0 ? '' :
    `{ productMetafield: { namespace: "custom", key: "for_him", value: "true" } }`;

  _filterFragmentCache = { color, design, collection, forHim };
  return _filterFragmentCache;
}

function invalidateFilterFragments(): void {
  _filterFragmentCache = null;
}

// GraphQL query builders
// handle: query another collection than the page's own (parent collection feature).
// idsOnly: return just product ids — used to diff the parent against the current collection.
interface QueryOptions {
  handle?: string;
  idsOnly?: boolean;
}

function buildQuery(first: number | string = 12, cursor: string | null = null, filters: string | null = null, queryAddon = '', opts: QueryOptions = {}): string {
  const handle = opts.handle ?? cfState.collectionHandle;
  const nodeFields = opts.idsOnly ? 'id' : buildProductNodeFields(queryAddon);
  return `
    query FilteredProducts @inContext(language: ${cfState.currentLanguage}, country: ${cfState.currentMarket}) {
      collection(handle: "${handle}") {
        products(first: ${first}, sortKey: COLLECTION_DEFAULT ${cursor ? `, after: "${cursor}"` : ''}${filters ? `, filters: ${filters}` : ''}) {
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            ${opts.idsOnly ? 'cursor' : ''}
            node {
              ${nodeFields}
            }
          }
        }
      }
    }
  `;
}

function buildProductNodeFields(queryAddon: string): string {
  return `id
              handle
              title
              availableForSale
              productType
              createdAt
              vendor
              tags
              ${queryAddon}
              metafield(namespace: "custom_fields", key: "related_products") {
                value
              }
              featuredImage {
                url
                altText
                id
              }
              priceRange {
                maxVariantPrice {
                  amount
                  currencyCode
                }
                minVariantPrice {
                  amount
                  currencyCode
                }
              }
              compareAtPriceRange {
                maxVariantPrice {
                  amount
                  currencyCode
                }
                minVariantPrice {
                  amount
                  currencyCode
                }
              }`;
}

function buildtcQuery(first: number | string = 12, cursor: string | null = null, opts: QueryOptions = {}): string {
  const { color: colorFilter, design: designFilter, collection: collectionFilter, forHim: forHimFilter } = getOrBuildFragments();
  const filters = `[{ productType: "Phone Case"}${colorFilter ? "," + colorFilter : ""}${designFilter ? "," + designFilter : ""}${collectionFilter ? "," + collectionFilter : ""}${forHimFilter ? "," + forHimFilter : ""}]`;
  // Option names/values in the catalog are English-only — @inContext(language: FR) does
  // NOT translate them (confirmed against the Storefront API), so these must never be
  // localized or variantBySelectedOptions silently returns null on the French storefront.
  const gloss = "Impact (Gloss)";
  const glossName = "Case Type";
  const deviceName = "Device";
  const deviceValue = CookieManager.getCookie('favPhone') || 'iPhone 17';
  // MagSafe Ring option values are "With"/"Without" in the catalog, not "Yes"/"No".
  const queryAddon = `variantYes: variantBySelectedOptions(selectedOptions: [{ name:"${deviceName}", value:"${deviceValue}"},{ name:"${glossName}", value:"${gloss}"},{ name:"MagSafe Ring", value:"With"}], ignoreUnknownOptions: true) {
    id
    image {
      url
      altText
    }
    ${PRE_ORDER_METAFIELDS}
  }
  variantNo: variantBySelectedOptions(selectedOptions: [{ name:"${deviceName}", value:"${deviceValue}"},{ name:"${glossName}", value:"${gloss}"},{ name:"MagSafe Ring", value:"Without"}], ignoreUnknownOptions: true) {
    id
    image {
      url
      altText
    }
    ${PRE_ORDER_METAFIELDS}
  }
  wishlistVariant: selectedOrFirstAvailableVariant {
    id
  }`;
  return buildQuery(first, cursor, filters, queryAddon, opts);
}

function buildcpcQuery(first: number | string = 12, cursor: string | null = null, opts: QueryOptions = {}): string {
  const { color: colorFilter, design: designFilter, collection: collectionFilter, forHim: forHimFilter } = getOrBuildFragments();
  const filters = `[{ productType: "Corpo Phone Case"}${colorFilter ? "," + colorFilter : ""}${designFilter ? "," + designFilter : ""}${collectionFilter ? "," + collectionFilter : ""}${forHimFilter ? "," + forHimFilter : ""}]`;
  // Option names/values in the catalog are English-only — @inContext(language: FR) does
  // NOT translate them (confirmed against the Storefront API), so these must never be
  // localized or variantBySelectedOptions silently returns null on the French storefront.
  const gloss = "Impact (Gloss)";
  const glossName = "Case Type";
  const deviceName = "Device";
  const queryAddon = `selectedOrFirstAvailableVariant(selectedOptions: [{ name:"${deviceName}", value:"${CookieManager.getCookie('favPhone') || 'iPhone 17'}"},{ name:"${glossName}", value:"${gloss}"}]) {
    id
    image {
      url
      altText
    }
    ${PRE_ORDER_METAFIELDS}
  }`;
  return buildQuery(first, cursor, filters, queryAddon, opts);
}

function buildccQuery(first: number | string = 12, cursor: string | null = null, opts: QueryOptions = {}): string {
  let phoneDeviceValue = CookieManager.getCookie('favPhone');
  if (!phoneDeviceValue) {
    phoneDeviceValue = 'iPhone 17';
  }
  if (!phoneDeviceValue.includes('iPhone')) {
    phoneDeviceValue = 'iPhone 17';
  }
  const { color: colorFilter, design: designFilter, collection: collectionFilter, forHim: forHimFilter } = getOrBuildFragments();
  const filters = `[{ productType: "Clear Case"},{variantOption: { name:"Device", value:"${phoneDeviceValue}"}}${colorFilter ? "," + colorFilter : ""}${designFilter ? "," + designFilter : ""}${collectionFilter ? "," + collectionFilter : ""}${forHimFilter ? "," + forHimFilter : ""}]`;

  let bumperType = 'Clear';
  const oldClearList = (document.querySelector('#old-clear') as HTMLInputElement).value;
  if (oldClearList.includes(`|${phoneDeviceValue}|`)) {
    bumperType = 'White';
  }
  const queryAddon = `selectedOrFirstAvailableVariant(selectedOptions: [{ name:"Device", value:"${phoneDeviceValue}"}, { name:"Bumper Color", value:"${bumperType}"}]) {
      id
      image {
        url
        altText
      }
      ${PRE_ORDER_METAFIELDS}
    }`;
  return buildQuery(first, cursor, filters, queryAddon, opts);
}

function buildacQuery(first: number | string = 12, cursor: string | null = null, opts: QueryOptions = {}): string {
  const { color: colorFilter, design: designFilter, collection: collectionFilter, forHim: forHimFilter } = getOrBuildFragments();
  const filters = `[{ productType: "Airpod Cases"}${colorFilter ? "," + colorFilter : ""}${designFilter ? "," + designFilter : ""}${collectionFilter ? "," + collectionFilter : ""}${forHimFilter ? "," + forHimFilter : ""}]`;
  const queryAddon = `images(first: 2) {
      edges {
        node {
          url
          altText
        }
      }
    }
    wishlistVariant: selectedOrFirstAvailableVariant {
      id
    }`;
  return buildQuery(first, cursor, filters, queryAddon, opts);
}

function buildmsQuery(first: number | string = 12, cursor: string | null = null, opts: QueryOptions = {}): string {
  const { color: colorFilter, design: designFilter, collection: collectionFilter, forHim: forHimFilter } = getOrBuildFragments();
  const filters = `[{ productType: "Skin"}${colorFilter ? "," + colorFilter : ""}${designFilter ? "," + designFilter : ""}${collectionFilter ? "," + collectionFilter : ""}${forHimFilter ? "," + forHimFilter : ""}]`;
  const queryAddon = `wishlistVariant: selectedOrFirstAvailableVariant {
      id
    }`;
  return buildQuery(first, cursor, filters, queryAddon, opts);
}

function buildaQuery(first: number | string = 12, cursor: string | null = null, opts: QueryOptions = {}): string {
  const filters = `[{ productType: "Phone accessories"},{ productType: "Hardshell Case"},{ productType: "Charms"},{ productType: "Screen Protector"},{ productType: "Gift Card"},{ productType: "Power Bank"},{ productType: ""},{ productType: "Phone Loop"},{ productType: "Crossbody phone strap"}]`;
  const queryAddon = `wishlistVariant: selectedOrFirstAvailableVariant {
      id
    }`;
  return buildQuery(first, cursor, filters, queryAddon, opts);
}

function buildicQuery(first: number | string = 12, cursor: string | null = null, opts: QueryOptions = {}): string {
  const { color: colorFilter, design: designFilter, collection: collectionFilter, forHim: forHimFilter } = getOrBuildFragments();
  const filters = `[{ productType: "iPad Folio Case"}${colorFilter ? "," + colorFilter : ""}${designFilter ? "," + designFilter : ""}${collectionFilter ? "," + collectionFilter : ""}${forHimFilter ? "," + forHimFilter : ""}]`;
  const queryAddon = `images (first: 3) {
    edges {
      node {
        id
        url
        altText
      }
    }
  }`;
  return buildQuery(first, cursor, filters, queryAddon, opts);
}

function buildiccQuery(first: number | string = 12, cursor: string | null = null, opts: QueryOptions = {}): string {
  const { color: colorFilter, design: designFilter, collection: collectionFilter, forHim: forHimFilter } = getOrBuildFragments();
  const filters = `[{ productType: "iPad Clear Case"}${colorFilter ? "," + colorFilter : ""}${designFilter ? "," + designFilter : ""}${collectionFilter ? "," + collectionFilter : ""}${forHimFilter ? "," + forHimFilter : ""}]`;
  return buildQuery(first, cursor, filters, '', opts);
}

function buildfcQuery(first: number | string = 12, cursor: string | null = null, opts: QueryOptions = {}): string {
  const { color: colorFilter, design: designFilter, collection: collectionFilter, forHim: forHimFilter } = getOrBuildFragments();
  const filters = `[{ productType: "Folio Case"},{productMetafield: {namespace: "custom", key: "folio_plain", value: "0"}}${colorFilter ? "," + colorFilter : ""}${designFilter ? "," + designFilter : ""}${collectionFilter ? "," + collectionFilter : ""}${forHimFilter ? "," + forHimFilter : ""}]`;
  let phoneDeviceValue = CookieManager.getCookie('favPhone');
  if (!phoneDeviceValue) {
    phoneDeviceValue = 'iPhone 17';
  }
  if (!phoneDeviceValue.includes('Galaxy S2') && !phoneDeviceValue.includes('iPhone')) {
    phoneDeviceValue = 'iPhone 17';
  }
  const queryAddon = `selectedOrFirstAvailableVariant(selectedOptions: [{ name:"Device", value:"${phoneDeviceValue}"}]) {
    id
    image {
      url
      altText
    }
    ${PRE_ORDER_METAFIELDS}
  }`;
  return buildQuery(first, cursor, filters, queryAddon, opts);
}

function buildfcuQuery(first: number | string = 12, cursor: string | null = null, opts: QueryOptions = {}): string {
  const { color: colorFilter, design: designFilter, collection: collectionFilter, forHim: forHimFilter } = getOrBuildFragments();
  const filters = `[{ productType: "Folio Case"},{productMetafield: {namespace: "custom", key: "folio_plain", value: "1"}}${colorFilter ? "," + colorFilter : ""}${designFilter ? "," + designFilter : ""}${collectionFilter ? "," + collectionFilter : ""}${forHimFilter ? "," + forHimFilter : ""}]`;
  let phoneDeviceValue = CookieManager.getCookie('favPhone');
  if (!phoneDeviceValue) {
    phoneDeviceValue = 'iPhone 17';
  }
  if (!phoneDeviceValue.includes('Galaxy S2') && !phoneDeviceValue.includes('iPhone')) {
    phoneDeviceValue = 'iPhone 17';
  }
  const queryAddon = `selectedOrFirstAvailableVariant(selectedOptions: [{ name:"Device", value:"${phoneDeviceValue}"}]) {
    id
    image {
      url
      altText
    }
    ${PRE_ORDER_METAFIELDS}
  }`;
  return buildQuery(first, cursor, filters, queryAddon, opts);
}

function buildkcQuery(first: number | string = 12, cursor: string | null = null, opts: QueryOptions = {}): string {
  const { color: colorFilter, design: designFilter, collection: collectionFilter, forHim: forHimFilter } = getOrBuildFragments();
  const filters = `[{ productType: "Kindle Case"}${colorFilter ? "," + colorFilter : ""}${designFilter ? "," + designFilter : ""}${collectionFilter ? "," + collectionFilter : ""}${forHimFilter ? "," + forHimFilter : ""}]`;
  const queryAddon = `images(first: 2) {
      edges {
        node {
          url
          altText
        }
      }
    }
    wishlistVariant: selectedOrFirstAvailableVariant {
      id
    }`;
  return buildQuery(first, cursor, filters, queryAddon, opts);
}

function buildcpQuery(first: number | string = 12, cursor: string | null = null, opts: QueryOptions = {}): string {
  const { color: colorFilter, design: designFilter, collection: collectionFilter, forHim: forHimFilter } = getOrBuildFragments();
  const filters = `[{ productType: "ChargePods"}${colorFilter ? "," + colorFilter : ""}${designFilter ? "," + designFilter : ""}${collectionFilter ? "," + collectionFilter : ""}${forHimFilter ? "," + forHimFilter : ""}]`;
  const queryAddon = `images(first: 2) {
      edges {
        node {
          url
          altText
        }
      }
    }
    wishlistVariant: selectedOrFirstAvailableVariant {
      id
    }`;
  return buildQuery(first, cursor, filters, queryAddon, opts);
}

// GraphQL fragment shared by every device-matched variant field (variantYes/variantNo/
// selectedOrFirstAvailableVariant) so the card can show a Coming Soon / Pre-Order badge
// for the specific variant the shopper is browsing — mirrors the PDP logic in
// snippets/product-info.liquid.
const PRE_ORDER_METAFIELDS = `notReleaseYet: metafield(namespace: "custom", key: "not_release_yet") {
      value
    }
    releaseDate: metafield(namespace: "custom", key: "release_date") {
      value
    }`;

// GraphQL product node types
interface GraphQLImageNode {
  url: string;
  altText: string | null;
  id?: string;
}

interface GraphQLImagesConnection {
  edges: { node: GraphQLImageNode }[];
}

interface GraphQLVariant {
  id: string;
  image: { url: string; altText: string | null } | null;
  notReleaseYet?: { value: string } | null;
  releaseDate?: { value: string } | null;
}

interface GraphQLPriceRange {
  maxVariantPrice: { amount: string; currencyCode: string };
  minVariantPrice: { amount: string; currencyCode: string };
}

interface GraphQLProduct {
  id: string;
  handle: string;
  title: string;
  availableForSale: boolean;
  productType: string;
  createdAt: string;
  vendor: string;
  tags: string[];
  featuredImage: GraphQLImageNode | null;
  priceRange: GraphQLPriceRange;
  compareAtPriceRange: GraphQLPriceRange;
  metafield: { value: string } | null;
  selectedOrFirstAvailableVariant?: GraphQLVariant | null;
  variantYes?: GraphQLVariant | null;
  variantNo?: GraphQLVariant | null;
  wishlistVariant?: { id: string } | null;
  images?: GraphQLImagesConnection;
}

// Compiled once at module level — used in every buildProductHtml call
const TITLE_STRIP_RE = /\s*(?:Clear Case|étui transparent|AirPods Case|iPad Folio|MacBook skin|Folio Case|ChargePods?).*$/u;

const CARD_IMAGE_WIDTHS = [200, 250, 300, 350, 500, 600, 700];
const CARD_IMAGE_SIZES = '(min-width: 1200px) 300px, (min-width: 990px) 250px, (min-width: 750px) 350px, (min-width: 500px) 250px, 200px';

function buildResponsiveCardImg(url: string, alt: string | null | undefined): string {
  const srcset = CARD_IMAGE_WIDTHS.map((w) => `${url}&width=${w} ${w}w`).join(', ');
  return `<img src="${url}&width=300" srcset="${srcset}" sizes="${CARD_IMAGE_SIZES}" alt="${alt}" loading="lazy">`;
}

function buildImage(currentProduct: GraphQLProduct): string {
  let imageurl = currentProduct.featuredImage ? currentProduct.featuredImage.url : '';
  let imagealt = currentProduct.featuredImage ? currentProduct.featuredImage.altText : '';
  const selectedVariant = currentProduct.variantYes || currentProduct.variantNo || currentProduct.selectedOrFirstAvailableVariant;
  if (selectedVariant) {
    imageurl = selectedVariant.image ? selectedVariant.image.url : '';
    imagealt = selectedVariant.image ? selectedVariant.image.altText : '';
  }
  let hoveraddon = '';
  if (currentProduct.productType == 'Folio Case' && currentProduct.featuredImage) {
    if (currentProduct.featuredImage) {
      const hoverURL = currentProduct.featuredImage.url;
      hoveraddon = `<div class="media media--transparent media--hover">
          ${buildResponsiveCardImg(hoverURL, hoverURL)}
        </div>`;
    }
  } else if (currentProduct.productType == 'iPad Folio Case') {
    const image = currentProduct.images?.edges[2]?.node;
    hoveraddon = '';
    if (image) {
      const hoverURL = image.url;
      const hoverAlt = image.altText;
      hoveraddon = `<div class="media media--transparent media--hover">
          ${buildResponsiveCardImg(hoverURL, hoverAlt ? hoverAlt : currentProduct.title)}
        </div>`;
    }
  } else if (currentProduct.productType == 'Airpod Cases') {
    const image = currentProduct.images?.edges[1]?.node;
    hoveraddon = '';
    if (image) {
      const hoverURL = image.url;
      const hoverAlt = image.altText;
      hoveraddon = `<div class="media media--transparent media--hover">
          ${buildResponsiveCardImg(hoverURL, hoverAlt ? hoverAlt : currentProduct.title)}
        </div>`;
    }
  } else if (currentProduct.productType == 'ChargePods') {
    const image = currentProduct.images?.edges[1]?.node;
    hoveraddon = '';
    if (image) {
      const hoverURL = image.url;
      const hoverAlt = image.altText;
      hoveraddon = `<div class="media media--transparent media--hover">
          ${buildResponsiveCardImg(hoverURL, hoverAlt ? hoverAlt : currentProduct.title)}
        </div>`;
    }
  }
  let imageSection = '';
  if (imageurl == '') {
    imageSection = `<div class="card__media">
        <div class="media media--transparent media--hover-effect --missing-image">
          <img src="${window.themeConfig?.assets?.loadingSpinner ?? ''}" alt="loading" width="75" height="75">
        </div>
    </div>`;
  } else {
    imageSection = `<div class="card__media">
      <div class="media media--transparent media--hover-effect">
        ${buildResponsiveCardImg(imageurl, imagealt)}
      </div>
      ${hoveraddon}
  </div>`;
  }

  return imageSection;
}

function buildProductHtml(product: GraphQLProduct, order: number): string {
  const { id, title, handle, availableForSale, selectedOrFirstAvailableVariant, variantYes, variantNo, wishlistVariant, tags, vendor, productType, priceRange, compareAtPriceRange, metafield } = product;

  const selectedVariant = variantYes || variantNo || selectedOrFirstAvailableVariant;
  const variantId = selectedVariant ? selectedVariant.id.replace("gid://shopify/ProductVariant/", "") : null;
  // Falls back to a dedicated aliased query field for product types whose card doesn't otherwise request a variant (Skin, Accessories, Airpod Cases, Kindle Case, ChargePods) — the wishlist-engine app requires a real variant id or the add silently fails.
  const wishlistVariantId = variantId || (wishlistVariant ? wishlistVariant.id.replace("gid://shopify/ProductVariant/", "") : null);
  let productUrl = handle;
  if (variantId) {
    productUrl = productUrl + '?variant=' + variantId;
  }

  const isEarlyAccess = vendor === 'Early Access';

  // Single-pass regex strip instead of 8 sequential indexOf/substring calls
  const new_title = title.replace(TITLE_STRIP_RE, '');

  let preprice = cfState.currentLanguage == 'FR' ? 'À partir de' : 'From';
  if (priceRange.maxVariantPrice.amount == priceRange.minVariantPrice.amount) {
    preprice = '';
  } else if (productType.indexOf('iPad') > -1) {
    preprice = '';
  }

  const urlPrefix = cfState.currentLanguage == 'FR' ? '/fr' : '';

  const hasForceBadge = (document.body as HTMLElement).dataset.forceBadge;
  let forceBadge = '';
  const hasForceBadeCondition = hasForceBadge == 'true' || (hasForceBadge as unknown) == true;

  if (hasForceBadeCondition && (document.body as HTMLElement).dataset.forceBadgeType!.includes(productType)) {
    forceBadge = `<span class="badge badge--${(document.body as HTMLElement).dataset.forceBadgeColor}">${(document.body as HTMLElement).dataset.forceBadgeText}</span>`;
  }

  // Coming Soon / Pre-Order — driven by the device-matched selected variant's
  // custom.not_release_yet + custom.release_date metafields, same rule as the PDP
  // (snippets/product-info.liquid): not_release_yet alone = Coming Soon, paired with a
  // known release_date = Pre-Order.
  const notReleaseYet = selectedVariant?.notReleaseYet?.value === 'true';
  const hasReleaseDate = !!selectedVariant?.releaseDate?.value;
  const comingSoon = notReleaseYet && !hasReleaseDate;
  const preOrder = notReleaseYet && hasReleaseDate;

  let oosBadge = '';
  let lowStockBadge = '';
  let newBadge = '';
  let colorLabel = '';
  let finalSaleBadge = '';
  let comingSoonBadge = '';
  let preOrderBadge = '';

  if (comingSoon) {
    comingSoonBadge = cfState.currentLanguage == 'FR' ? '<span class="badge">Arrive bientôt</span>' : '<span class="badge">Coming Soon</span>';
  } else if (preOrder) {
    preOrderBadge = `<span class="badge">${window.variantStrings?.preOrder ?? (cfState.currentLanguage == 'FR' ? 'Précommande' : 'Pre order')}</span>`;
  }

  // tags.includes avoids allocating filter result arrays
  // Skip "Out of Stock" when the selected variant is simply not released yet —
  // it isn't sold out, it hasn't launched.
  if (!availableForSale && !notReleaseYet) {
    oosBadge = cfState.currentLanguage == 'FR' ? '<span class="badge">Épuisé</span>' : '<span class="badge">Out of Stock</span>';
  } else if (tags.includes('low-stock') && !notReleaseYet) {
    // Skip "Low Stock" for a not-yet-released variant too — same reasoning as the
    // "Out of Stock" guard above: it hasn't launched, so it can't be running low.
    lowStockBadge = cfState.currentLanguage == 'FR' ? '<span class="badge">Bientôt épuisé</span>' : '<span class="badge">Low Stock</span>';
  }

  if (tags.includes('New')) {
    newBadge = cfState.currentLanguage == 'FR' ? '<span class="badge">Nouveau</span>' : '<span class="badge">New</span>';
  }

  if (tags.includes('final_sale')) {
    finalSaleBadge = cfState.currentLanguage == 'FR' ? '<span class="badge">Vente finale</span>' : '<span class="badge">Final Sale</span>';
  }

  const quickAddLabel = cfState.currentLanguage == 'FR' ? 'Ajout rapide' : 'Quick Add';
  if (metafield && metafield.value) {
    colorLabel = cfState.currentLanguage == 'FR'
      ? `<div class="card__label">Plus de couleurs disponibles</div>`
      : `<div class="card__label">More colors available</div>`;
  }

  const productID = id.replace("gid://shopify/Product/", "");
  const quickAdd = `<div class="quick-add no-js-hidden">
      <modal-opener data-modal="#QuickAdd-${productID}">
        <button id="quick-add-template--${productID}-submit"
          type="submit" name="add"
          class="quick-add__submit button button--full-width button--outline" aria-haspopup="dialog"
          aria-labelledby="quick-add-template--${productID} title-template--${productID}"
          data-product-url="${urlPrefix}/products/${productUrl}">
          ${quickAddLabel}
          <div class="loading__spinner hidden">
            <img src="${window.themeConfig?.assets?.loadingSpinner ?? ''}" alt="loading" width="75" height="75">
          </div>
        </button>
      </modal-opener>
      <quick-add-modal id="QuickAdd-${productID}" class="quick-add-modal">
        <div
          role="dialog"
          aria-label="${new_title}"
          aria-modal="true"
          class="quick-add-modal__content global-settings-popup"
          tabindex="-1"
        >
          <button
            id="ModalClose-${productID}"
            type="button"
            class="quick-add-modal__toggle button button--outline"
            aria-label="Close"
          >
            <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" class="icon icon-close" fill="none" viewBox="0 0 18 17">
              <path d="M.865 15.978a.5.5 0 00.707.707l7.433-7.431 7.579 7.282a.501.501 0 00.846-.37.5.5 0 00-.153-.351L9.712 8.546l7.417-7.416a.5.5 0 10-.707-.708L8.991 7.853 1.413.573a.5.5 0 10-.693.72l7.563 7.268-7.418 7.417z" fill="currentColor">
            </svg>
          </button>
          <div id="QuickAddInfo-${productID}" class="quick-add-modal__content-info"></div>
        </div>
      </quick-add-modal>
    </div>`;
  const clearCondition = productType.indexOf('Clear Case') > -1 && productType.indexOf('iPad') == -1;

  let price: number | string = clearCondition ? 34.99 : priceRange.minVariantPrice.amount;
  if (title.indexOf("DIY Queen Clear Case") > -1 || title.indexOf("DIY Queen transparent") > -1) {
    price = 39.95;
  }
  if (productType.indexOf('ChargePods') > -1) {
    price = 79.99;
    preprice = "";
  }

  const enPriceTag = cfState.currentLanguage == 'EN' ? '$' : '';
  const frPriceTag = cfState.currentLanguage == 'FR' ? '$' : '';
  const currencyTag = cfState.currentMarket === 'CA' ? ' CAD' : ' USD';
  price = parseFloat(String(price)).toFixed(2);

  const compareAtAmount = parseFloat(compareAtPriceRange?.maxVariantPrice?.amount || '0');
  const maxPrice = compareAtAmount.toFixed(2);
  const showSalePricing = productType.indexOf('ChargePods') === -1 && productType.indexOf('AirGrip') === -1 && compareAtAmount > parseFloat(String(price));

  let contentHTML: string;
  if (showSalePricing) {
    contentHTML = `<div class="price price--on-sale">
                  <div class="price__container">
                    <div class="price__sale">
                      ${enPriceTag}${price}${frPriceTag}${currencyTag}
                    </div>
                    <s class="price__compare">
                      ${enPriceTag}${maxPrice}${frPriceTag}${currencyTag}
                    </s>
                  </div>
                </div>
                ${colorLabel}`;
  } else {
    contentHTML = `<div class="price">
                  <div class="price__container">
                    <div class="price__regular">
                      ${preprice} ${enPriceTag}${price}${frPriceTag}${currencyTag}
                    </div>
                  </div>
                </div>
                ${colorLabel}`;
  }

  if (isEarlyAccess) {
    contentHTML = `<span class="card__badge badge-early-access">${cfState.currentLanguage == 'EN' ? 'Coming Soon' : 'Bientôt disponible'}</span>`;
  }

  return `
    <li class="grid__item" style="order: ${order}" data-order="${order}">
      <div class="card-wrapper product-card-wrapper underline-links-hover ${productType.indexOf('Airpod') > -1 ? '--airpods' : ''} ${productType.indexOf('iPad') > -1 ? '--ipad' : ''}">
        <a href="${urlPrefix}/products/${productUrl}">
          <span class="visually-hidden">${new_title}</span>
        </a>
        <div class="wishlist-engine" data-product_id="${productID}" data-variant_id="${wishlistVariantId}" data-full_button="false" data-css="true"></div>
        <div class="card card--standard card--media">
          <div class="card__inner">
            ${buildImage(product)}
          </div>
          <div class="card__content">
            <div class="card__information">
              <h3 class="card__heading">
                <a href="${productUrl}" class="full-unstyled-link">
                  ${new_title}
                </a>
              </h3>
              <div class="card-information">
                ${contentHTML}
              </div>
            </div>
            <!--${quickAdd}-->
          </div>
          <div class="card__badge top left">
            ${newBadge}
            ${lowStockBadge}
            ${oosBadge}
            ${comingSoonBadge}
            ${preOrderBadge}
            ${forceBadge}
            ${finalSaleBadge}
          </div>
        </div>
      </div>
    </li>
  `;
}

// Interface used by FilterManager and LazyLoader to reference product type
// managers without a forward-class dependency. ProductTypeManager satisfies
// this structurally; no explicit `implements` needed.
interface ProductTypeManagerLike {
  section: HTMLElement | null;
  isValid: boolean;
  isLoading: boolean;
  sectionType: string;
  lazyLoader: { disconnect(): void } | null;
  loadMoreContent(isReset?: boolean): void;
}
type ProductTypeManagerRef = ProductTypeManagerLike | null;

class FilterManager {
  ImpactManager: ProductTypeManagerRef = null;
  CorpoPhoneCaseManager: ProductTypeManagerRef = null;
  FolioManager: ProductTypeManagerRef = null;
  FolioUnisManager: ProductTypeManagerRef = null;
  ClearCaseManager: ProductTypeManagerRef = null;
  AirPodsManager: ProductTypeManagerRef = null;
  IPadManager: ProductTypeManagerRef = null;
  IPadClearManager: ProductTypeManagerRef = null;
  SkinManager: ProductTypeManagerRef = null;
  AccessoriesManager: ProductTypeManagerRef = null;
  ChargePodsManager: ProductTypeManagerRef = null;
  KindleManager: ProductTypeManagerRef = null;
  currentActiveSection = "all";
  productTypeContainerEl: Element | null;
  productTypeToggleEl: Element | null;
  devicesContainerEl: Element | null;
  devicesToggleEl: Element | null;
  debouncedResetSections: () => void;

  constructor() {
    this.productTypeContainerEl = document.querySelector(cfSelectors.filters.productTypeContainer);
    this.productTypeToggleEl = document.querySelector(cfSelectors.filters.toggleProductType);
    this.devicesContainerEl = document.querySelector(cfSelectors.filters.devicesContainer);
    this.devicesToggleEl = document.querySelector(cfSelectors.filters.toggleDevices);
    this.debouncedResetSections = debounce(() => this.resetSections(), 150);
    this.initializePhoneSelector();
    this.initializeEventListeners();
    if (this.productTypeContainerEl) {
      this.initializePTFilters();
    }
  }

  setManagers(
    ImpactManager: ProductTypeManagerRef,
    CorpoPhoneCaseManager: ProductTypeManagerRef,
    FolioManager: ProductTypeManagerRef,
    FolioUnisManager: ProductTypeManagerRef,
    ClearCaseManager: ProductTypeManagerRef,
    AirPodsManager: ProductTypeManagerRef,
    IPadManager: ProductTypeManagerRef,
    IPadClearManager: ProductTypeManagerRef,
    SkinManager: ProductTypeManagerRef,
    AccessoriesManager: ProductTypeManagerRef,
    ChargePodsManager: ProductTypeManagerRef,
    KindleManager: ProductTypeManagerRef
  ): void {
    this.ImpactManager = ImpactManager;
    this.CorpoPhoneCaseManager = CorpoPhoneCaseManager;
    this.FolioManager = FolioManager;
    this.FolioUnisManager = FolioUnisManager;
    this.ClearCaseManager = ClearCaseManager;
    this.AirPodsManager = AirPodsManager;
    this.IPadManager = IPadManager;
    this.IPadClearManager = IPadClearManager;
    this.SkinManager = SkinManager;
    this.AccessoriesManager = AccessoriesManager;
    this.ChargePodsManager = ChargePodsManager;
    this.KindleManager = KindleManager;
  }

  initializeEventListeners(): void {
    const { toggleDevices, toggleProductType, devicesContainer, productTypeContainer } = cfSelectors.filters;
    document.querySelector(toggleDevices)?.addEventListener('click', () =>
      this.toggleFilter(devicesContainer, toggleDevices, toggleProductType, productTypeContainer));

    document.querySelector(toggleProductType)?.addEventListener('click', () =>
      this.toggleFilter(productTypeContainer, toggleProductType, toggleDevices, devicesContainer));
  }

  toggleFilter(targetContainer: string, activeToggle: string, otherToggle: string, otherContainer: string): void {
    const container = document.querySelector(targetContainer);
    const toggle = document.querySelector(activeToggle);

    container?.classList.toggle('--open');
    toggle?.classList.toggle('--active');

    document.querySelector(otherToggle)?.classList.remove('--active');
    document.querySelector(otherContainer)?.classList.remove('--open');
  }

  initializePhoneSelector(): void {
    const phoneSelector = document.querySelector(cfSelectors.filters.phoneDevice) as HTMLSelectElement | null;
    if (phoneSelector) {
      if (phoneSelector.options.length > 0) {
        const currentFavPhone = CookieManager.getCookie('favPhone') || 'iPhone 17';
        phoneSelector.value = currentFavPhone;
      } else {
        phoneSelector.parentElement?.parentElement?.parentElement?.parentElement?.parentElement?.remove();
        document.querySelector('[data-toggle-device]')?.remove();
      }
    }
    const allSelect = document.querySelectorAll('.facets-container.custom select');
    for (const select of Array.from(allSelect)) {
      new Choices(select as HTMLSelectElement, { shouldSort: false, shouldSortItems: false, placeholder: (select.previousElementSibling as HTMLElement)?.innerHTML });
      this.setupSelectorEvents(select as HTMLSelectElement);
    }
  }

  setupSelectorEvents(selector: HTMLSelectElement): void {
    const isPhoneSelector = selector.matches(cfSelectors.filters.phoneDevice);

    if (isPhoneSelector) {
      selector.addEventListener('change', () => {
        CookieManager.setCookie('favPhone', selector.value);
        // Phone changes don't affect filter fragments — no invalidation needed
        this.ImpactManager?.isValid && !this.ImpactManager?.isLoading ? this.ImpactManager.loadMoreContent(true) : null;
        this.CorpoPhoneCaseManager?.isValid && !this.CorpoPhoneCaseManager?.isLoading ? this.CorpoPhoneCaseManager.loadMoreContent(true) : null;
        this.ClearCaseManager?.isValid && !this.ClearCaseManager?.isLoading ? this.ClearCaseManager.loadMoreContent(true) : null;
        this.FolioManager?.isValid && !this.FolioManager?.isLoading ? this.FolioManager.loadMoreContent(true) : null;
        this.FolioUnisManager?.isValid && !this.FolioUnisManager?.isLoading ? this.FolioUnisManager.loadMoreContent(true) : null;
      });
    } else {
      selector.addEventListener('change', () => {
        let shouldReload = false;
        if (selector.id && (selector.id.startsWith('color') || selector.id.startsWith('couleur'))) {
          const selectedOptions = Array.from(selector.selectedOptions || []);
          const values = selectedOptions
            .map(option => option.value)
            .filter(value => value && value.trim() !== '');
          const next = values.length > 0 ? values : [];
          if (!arraysEqual(next, cfState.activeColorFilter)) {
            cfState.activeColorFilter = next;
            invalidateFilterFragments();
            shouldReload = true;
          }
        } else if (selector.id && selector.id.startsWith('design')) {
          const selectedOptions = Array.from(selector.selectedOptions || []);
          const values = selectedOptions
            .map(option => option.value)
            .filter(value => value && value.trim() !== '');
          const next = values.length > 0 ? values : [];
          if (!arraysEqual(next, cfState.activeDesignFilter)) {
            cfState.activeDesignFilter = next;
            invalidateFilterFragments();
            shouldReload = true;
          }
        }
        if (shouldReload) {
          this.debouncedResetSections();
        }
      });
    }
  }

  initializePTFilters(): void {
    const productTypeSelector = document.querySelector(cfSelectors.filters.productTypeContainer);
    if (!productTypeSelector) return;

    productTypeSelector.querySelectorAll('[data-target]').forEach(el => {
      el.addEventListener('click', (e) => {
        e.preventDefault();
        const target = (el as HTMLElement).dataset.target!;
        if (target !== 'all') {
          this.setActiveManager(target);
        } else {
          this.resetSections();
        }
        this.setActiveSection(target);
      });
    });
  }

  showProductTypeFilters = (): void => {
    if (this.productTypeContainerEl && this.productTypeContainerEl.querySelectorAll('[data-target]:not(.--hidden)').length > 2) {
      this.productTypeContainerEl.classList.remove('--hidden');
      this.productTypeToggleEl?.classList.remove('--hidden');
    }
  };

  hideProductTypeFilters = (): void => {
    // Use cached refs instead of re-querying the DOM
    if (this.productTypeContainerEl) this.productTypeContainerEl.classList.add('--hidden');
    if (this.productTypeToggleEl) this.productTypeToggleEl.classList.add('--hidden');
  };

  showProductTypeFilter = (currentType: string): void => {
    const targetEl = document.querySelector(`[data-target="${currentType}"]`);
    if (targetEl && targetEl.parentElement) {
      targetEl.parentElement.removeAttribute('hidden');
    }
    this.showProductTypeFilters();
  };

  hideProductTypeFilter = (currentType: string): void => {
    const targetEl = document.querySelector(`[data-target="${currentType}"]`);
    if (targetEl && targetEl.parentElement) {
      targetEl.parentElement.setAttribute('hidden', 'hidden');
    }
  };

  setActiveManager(targetType: string): void {
    switch (targetType) {
      case 'tc':
        this.ImpactManager?.isValid && this.ImpactManager.section?.dataset.hasNextPage != "false" ? this.ImpactManager.lazyLoader = new LazyLoader(this.ImpactManager) : null;
        break;
      case 'cpc':
        this.CorpoPhoneCaseManager?.isValid && this.CorpoPhoneCaseManager.section?.dataset.hasNextPage != "false" ? this.CorpoPhoneCaseManager.lazyLoader = new LazyLoader(this.CorpoPhoneCaseManager) : null;
        break;
      case 'cc':
        this.ClearCaseManager?.isValid && this.ClearCaseManager.section?.dataset.hasNextPage != "false" ? this.ClearCaseManager.lazyLoader = new LazyLoader(this.ClearCaseManager) : null;
        break;
      case 'fc':
        this.FolioManager?.isValid && this.FolioManager.section?.dataset.hasNextPage != "false" ? this.FolioManager.lazyLoader = new LazyLoader(this.FolioManager) : null;
        break;
      case 'fcu':
        this.FolioUnisManager?.isValid && this.FolioUnisManager.section?.dataset.hasNextPage != "false" ? this.FolioUnisManager.lazyLoader = new LazyLoader(this.FolioUnisManager) : null;
        break;
      case 'ac':
        this.AirPodsManager?.isValid && this.AirPodsManager.section?.dataset.hasNextPage != "false" ? this.AirPodsManager.lazyLoader = new LazyLoader(this.AirPodsManager) : null;
        break;
      case 'ic':
        this.IPadManager?.isValid && this.IPadManager.section?.dataset.hasNextPage != "false" ? this.IPadManager.lazyLoader = new LazyLoader(this.IPadManager) : null;
        break;
      case 'icc':
        this.IPadClearManager?.isValid && this.IPadClearManager.section?.dataset.hasNextPage != "false" ? this.IPadClearManager.lazyLoader = new LazyLoader(this.IPadClearManager) : null;
        break;
      case 'ms':
        this.SkinManager?.isValid && this.SkinManager.section?.dataset.hasNextPage != "false" ? this.SkinManager.lazyLoader = new LazyLoader(this.SkinManager) : null;
        break;
      case 'a':
        this.AccessoriesManager?.isValid && this.AccessoriesManager.section?.dataset.hasNextPage != "false" ? this.AccessoriesManager.lazyLoader = new LazyLoader(this.AccessoriesManager) : null;
        break;
      case 'cp':
        this.ChargePodsManager?.isValid && this.ChargePodsManager.section?.dataset.hasNextPage != "false" ? this.ChargePodsManager.lazyLoader = new LazyLoader(this.ChargePodsManager) : null;
        break;
      case 'kc':
        this.KindleManager?.isValid && this.KindleManager.section?.dataset.hasNextPage != "false" ? this.KindleManager.lazyLoader = new LazyLoader(this.KindleManager) : null;
        break;
      default:
        break;
    }
  }

  setActiveSection(sectionAbbv: string): void {
    if (this.currentActiveSection == sectionAbbv) return;

    if (sectionAbbv == 'all') {
      document.querySelectorAll('[data-section]').forEach(element => {
        element.classList.remove('--section-hidden');
      });
    } else {
      document.querySelectorAll('[data-section]').forEach(element => {
        if ((element as HTMLElement).dataset.section !== sectionAbbv) {
          element.classList.add('--section-hidden');
        } else {
          element.classList.remove('--section-hidden');
        }
      });
    }

    const productTypeSelector = document.querySelector(cfSelectors.filters.productTypeContainer);
    if (productTypeSelector) {
      const activeBtn = productTypeSelector.querySelector('[data-target].active');
      const nextActive = productTypeSelector.querySelector(`[data-target="${sectionAbbv}"]`);
      if (activeBtn) activeBtn.classList.remove('active');
      if (nextActive) nextActive.classList.add('active');
    }
    this.currentActiveSection = sectionAbbv;
  }

  resetSections(): void {
    document.querySelectorAll(cfSelectors.sections).forEach(element => {
      const el = element as HTMLElement;
      el.dataset.cursor = '';
      el.dataset.hasNextPage = '';
      const currentSection = el.dataset.section;
      switch (currentSection) {
        case 'tc':
          this.ImpactManager?.isValid ? this.ImpactManager.loadMoreContent(true) : null;
          break;
        case 'cpc':
          this.CorpoPhoneCaseManager?.isValid ? this.CorpoPhoneCaseManager.loadMoreContent(true) : null;
          break;
        case 'cc':
          this.ClearCaseManager?.isValid ? this.ClearCaseManager.loadMoreContent(true) : null;
          break;
        case 'fc':
          this.FolioManager?.isValid ? this.FolioManager.loadMoreContent(true) : null;
          break;
        case 'fcu':
          this.FolioUnisManager?.isValid ? this.FolioUnisManager.loadMoreContent(true) : null;
          break;
        case 'ac':
          this.AirPodsManager?.isValid ? this.AirPodsManager.loadMoreContent(true) : null;
          break;
        case 'ic':
          this.IPadManager?.isValid ? this.IPadManager.loadMoreContent(true) : null;
          break;
        case 'icc':
          this.IPadClearManager?.isValid ? this.IPadClearManager.loadMoreContent(true) : null;
          break;
        case 'ms':
          this.SkinManager?.isValid ? this.SkinManager.loadMoreContent(true) : null;
          break;
        case 'a':
          this.AccessoriesManager?.isValid ? this.AccessoriesManager.loadMoreContent(true) : null;
          break;
        case 'cp':
          this.ChargePodsManager?.isValid ? this.ChargePodsManager.loadMoreContent(true) : null;
          break;
        case 'kc':
          this.KindleManager?.isValid ? this.KindleManager.loadMoreContent(true) : null;
          break;
      }
      element.classList.remove('--section-hidden');
    });
  }
}

// Reads the checked filter inputs into cfState without reloading the lists
function syncFilterStateFromCheckboxes(filtersContainer: Element): void {
  const checkedInputs = filtersContainer.querySelectorAll('input[type="checkbox"]:checked');
  const colorValues: string[] = [];
  const designValues: string[] = [];
  const collectionValues: string[] = [];
  const forHimValues: string[] = [];

  checkedInputs.forEach((input) => {
    const name = (input as HTMLInputElement).name || '';
    const key = name.split('.').pop() || '';
    const value = (input as HTMLInputElement).value;
    if (!value) return;

    const lowerKey = key.toLowerCase();
    if (lowerKey.includes('color') || lowerKey.includes('couleur')) {
      colorValues.push(value);
    } else if (lowerKey.includes('design') || lowerKey.includes('motif')) {
      designValues.push(value);
    } else if (lowerKey.includes('collections')) {
      collectionValues.push(value);
    } else if (lowerKey.includes('for_him') || lowerKey.includes('for-him') || lowerKey.includes('pour_lui') || lowerKey.includes('pour-lui')) {
      forHimValues.push(value);
    }
  });

  cfState.activeColorFilter = colorValues;
  cfState.activeDesignFilter = designValues;
  cfState.activeCollectionFilter = collectionValues;
  cfState.activeForHimFilter = forHimValues;
  invalidateFilterFragments();
}

function initializeNewFiltersIntegration(filterManager: FilterManager, wasRestored: boolean): void {
  const filtersContainer = document.querySelector('.filters-container');
  if (!filtersContainer) return;

  const updateFilterSummaryLabels = (): void => {
    const checkedInputs = filtersContainer.querySelectorAll('input[type="checkbox"]:checked');
    const selectionsByFilter = new Map<Element, string[]>();

    checkedInputs.forEach((input) => {
      const filterEl = input.closest('.js-filter');
      if (!filterEl) return;

      const labelEl = input.closest('label');
      const valueLabel = labelEl ? labelEl.textContent!.trim() : ((input as HTMLInputElement).value || '');
      if (!valueLabel) return;

      if (!selectionsByFilter.has(filterEl)) {
        selectionsByFilter.set(filterEl, []);
      }
      selectionsByFilter.get(filterEl)!.push(valueLabel);
    });

    const resetButton = filtersContainer.querySelector('[data-reset-filters]');

    filtersContainer.querySelectorAll('.js-filter').forEach((filterEl) => {
      if (filterEl.querySelector('#phone-device')) return;

      const activeSpan = filterEl.querySelector('.filter__active');
      if (!activeSpan) return;

      const values = selectionsByFilter.get(filterEl) || [];
      if (values.length === 0) {
        activeSpan.textContent = '';
      } else if (values.length === 1) {
        activeSpan.textContent = values[0];
      } else {
        activeSpan.textContent = `(${values.length})`;
      }
    });

    if (resetButton) {
      const hasActiveNonDeviceFilters = checkedInputs.length > 0;
      resetButton.classList.toggle('--hidden', !hasActiveNonDeviceFilters);
    }
  };

  const updateStateFromNewFilters = (): void => {
    const filtersLoader = filtersContainer.querySelector('.filters__loader');
    if (filtersLoader) {
      filtersLoader.classList.add('--open');
    }

    syncFilterStateFromCheckboxes(filtersContainer);

    filterManager.debouncedResetSections();
    updateFilterSummaryLabels();
  };

  const newPhoneSelector = filtersContainer.querySelector('#phone-device') as HTMLSelectElement | null;
  if (newPhoneSelector) {
    const currentFavPhone = CookieManager.getCookie('favPhone') || 'iPhone 17';
    newPhoneSelector.value = currentFavPhone;

    let drawerChoices: ChoicesInstance | null = null;
    try {
      drawerChoices = new Choices(newPhoneSelector, { shouldSort: false, shouldSortItems: false, placeholder: (newPhoneSelector.previousElementSibling as HTMLElement)?.innerHTML || '' });
    } catch (e) {
      console.warn('Choices initialization failed for new phone selector', e);
    }

    // Optional copy of the device select next to the filter button
    const deviceDuplicate = linkDeviceSelectDuplicate(newPhoneSelector, drawerChoices);

    const deviceFilter = newPhoneSelector.closest('.js-filter');
    if (deviceFilter) {
      const activeSpan = deviceFilter.querySelector('.filter__active');
      if (activeSpan) {
        const selectedOption = newPhoneSelector.options[newPhoneSelector.selectedIndex];
        const label = selectedOption ? selectedOption.textContent!.trim() : '';
        activeSpan.textContent = label;
      }
    }

    newPhoneSelector.addEventListener('change', () => {
      const filtersLoader = filtersContainer.querySelector('.filters__loader');
      if (filtersLoader) {
        filtersLoader.classList.add('--open');
      }

      CookieManager.setCookie('favPhone', newPhoneSelector.value);
      deviceDuplicate?.syncFromDrawer();
      filterManager.ImpactManager?.isValid && !filterManager.ImpactManager?.isLoading ? filterManager.ImpactManager.loadMoreContent(true) : null;
      filterManager.ClearCaseManager?.isValid && !filterManager.ClearCaseManager?.isLoading ? filterManager.ClearCaseManager.loadMoreContent(true) : null;
      filterManager.FolioManager?.isValid && !filterManager.FolioManager?.isLoading ? filterManager.FolioManager.loadMoreContent(true) : null;
      filterManager.FolioUnisManager?.isValid && !filterManager.FolioUnisManager?.isLoading ? filterManager.FolioUnisManager.loadMoreContent(true) : null;

      const deviceFilter = newPhoneSelector.closest('.js-filter');
      if (deviceFilter) {
        const activeSpan = deviceFilter.querySelector('.filter__active');
        if (activeSpan) {
          const selectedOption = newPhoneSelector.options[newPhoneSelector.selectedIndex];
          const label = selectedOption ? selectedOption.textContent!.trim() : '';
          activeSpan.textContent = label;
        }
      }
    });
  }

  filtersContainer.addEventListener('change', (event) => {
    const target = event.target as HTMLElement;
    if (!target || !target.matches('input[type="checkbox"]')) return;
    updateStateFromNewFilters();
  });

  const resetButton = filtersContainer.querySelector('[data-reset-filters]');
  if (resetButton) {
    resetButton.addEventListener('click', () => {
      const filtersLoader = filtersContainer.querySelector('.filters__loader');
      if (filtersLoader) {
        filtersLoader.classList.add('--open');
      }

      const checkedInputs = filtersContainer.querySelectorAll('input[type="checkbox"]:checked');
      checkedInputs.forEach((input) => {
        (input as HTMLInputElement).checked = false;
      });

      cfState.activeColorFilter = [];
      cfState.activeDesignFilter = [];
      cfState.activeCollectionFilter = [];
      cfState.activeForHimFilter = [];
      invalidateFilterFragments();

      updateFilterSummaryLabels();
      filterManager.debouncedResetSections();
    });
  }

  if (wasRestored) {
    // restoreCollectionState already put back the filters the lists were fetched with. The URL
    // auto-select below would reset the lists under the restored scroll position.
    updateFilterSummaryLabels();
    return;
  }

  // Auto-select filters from URL query params (?color=Red&design=Floral&for_him=true)
  const urlParams = new URLSearchParams(window.location.search);
  const colorParam = urlParams.get('color');
  const designParam = urlParams.get('design');
  const forHimParam = urlParams.get('for_him');
  let needsUpdate = false;

  if (colorParam) {
    const colorCheckbox = filtersContainer.querySelector(`input[type="checkbox"][name="filter.p.m.custom.colors"][value="${colorParam}"]`) as HTMLInputElement | null;
    if (colorCheckbox && !colorCheckbox.checked) {
      colorCheckbox.checked = true;
      needsUpdate = true;
    }
  }

  if (designParam) {
    const designCheckbox = filtersContainer.querySelector(`input[type="checkbox"][value="${designParam}"]`) as HTMLInputElement | null;
    if (designCheckbox && !designCheckbox.checked) {
      const name = (designCheckbox.name || '').toLowerCase();
      if (name.includes('design') || name.includes('motif')) {
        designCheckbox.checked = true;
        needsUpdate = true;
      }
    }
  }

  if (forHimParam) {
    const forHimCheckbox = (
      filtersContainer.querySelector('input[type="checkbox"][name="filter.p.m.custom.for-him"]') ||
      filtersContainer.querySelector('input[type="checkbox"][name="filter.p.m.custom.pour-lui"]')
    ) as HTMLInputElement | null;
    if (forHimCheckbox && !forHimCheckbox.checked) {
      forHimCheckbox.checked = true;
      needsUpdate = true;
    }
  }

  if (needsUpdate) {
    updateStateFromNewFilters();
  }

  updateFilterSummaryLabels();
}

function initializeCollectionsFilterHydration(): void {
  const filtersContainer = document.querySelector('.filters-container');
  if (!filtersContainer) return;

  hydrateCollectionsFilterUI(filtersContainer);

  const debouncedHydrate = debounce(() => hydrateCollectionsFilterUI(filtersContainer), 100);
  const observer = new MutationObserver(() => debouncedHydrate());
  observer.observe(filtersContainer, { childList: true, subtree: true });
}

interface GraphQLProductsResponse {
  data?: {
    collection?: {
      products?: {
        pageInfo: { hasNextPage: boolean; endCursor: string };
        edges: { node: GraphQLProduct }[];
      };
    };
  };
}

// The wishlist-engine app only binds its click handlers to .wishlist-engine elements present
// at the time its bootstrap script runs — it never observes the DOM for later additions, and
// that bootstrap script removes itself from the DOM once done. window.WishEngineinit is the
// app's own re-hydration entry point; call it after every grid update so newly injected cards
// get bound too.
function reinitWishlistWidget(): void {
  const init = (window as unknown as { WishEngineinit?: () => void }).WishEngineinit;
  if (typeof init !== 'function') return;
  try {
    init();
  } catch (e) {
    console.warn('WishEngineinit failed', e);
  }
}

function buildTypeQuery(sectionType: string, first: number | string, cursor: string | null, opts: QueryOptions = {}): string | undefined {
  switch (sectionType) {
    case 'tc': return buildtcQuery(first, cursor, opts);
    case 'cpc': return buildcpcQuery(first, cursor, opts);
    case 'fc': return buildfcQuery(first, cursor, opts);
    case 'fcu': return buildfcuQuery(first, cursor, opts);
    case 'cc': return buildccQuery(first, cursor, opts);
    case 'ac': return buildacQuery(first, cursor, opts);
    case 'ic': return buildicQuery(first, cursor, opts);
    case 'icc': return buildiccQuery(first, cursor, opts);
    case 'ms': return buildmsQuery(first, cursor, opts);
    case 'a': return buildaQuery(first, cursor, opts);
    case 'cp': return buildcpQuery(first, cursor, opts);
    case 'kc': return buildkcQuery(first, cursor, opts);
  }
  return undefined;
}

interface GraphQLIdsResponse {
  data?: {
    collection?: {
      products?: {
        pageInfo: { hasNextPage: boolean; endCursor: string };
        edges: { cursor: string; node: { id: string } }[];
      };
    } | null;
  };
}

async function fetchIdsPage(query: string | undefined): Promise<IdsPage> {
  if (!query) return { ids: [], cursors: [], hasNextPage: false, endCursor: null };
  const response = await fetch(cfState.shopUrl + '/api/2026-04/graphql.json', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': cfState.accessToken
    },
    body: JSON.stringify({ query })
  });
  if (!response.ok) throw new Error('Network response was not ok');
  const data: GraphQLIdsResponse = await response.json();
  const products = data.data?.collection?.products;
  if (!products) return { ids: [], cursors: [], hasNextPage: false, endCursor: null };
  return {
    ids: products.edges.map(({ node }) => node.id),
    cursors: products.edges.map(({ cursor }) => cursor),
    hasNextPage: products.pageInfo.hasNextPage,
    endCursor: products.pageInfo.endCursor
  };
}

const ID_PAGE_SIZE = 250;

// Null unless the section rendered a parent collection (setting on + metafield set)
const parentCollection = ParentCollection.fromDom(
  document.querySelector('[data-parent-handle]') as HTMLElement | null,
  {
    fetchCurrentIds: (cursor) => fetchIdsPage(buildQuery(ID_PAGE_SIZE, cursor, null, '', { idsOnly: true })),
    fetchParentIds: (sectionType, cursor) =>
      fetchIdsPage(buildTypeQuery(sectionType, ID_PAGE_SIZE, cursor, { handle: parentCollection!.handle, idsOnly: true }))
  }
);

// Grid cells a promo takes up: its width in columns (2-col grid below 990px, 4-col above)
function promoCells(size: string): number {
  const desktop = window.matchMedia('(min-width: 990px)').matches;
  if (size === 'one') return 1;
  if (size === 'two') return desktop ? 2 : 1;
  return desktop ? 4 : 2;
}

// A promo already placed after the previous batch's last product (see trailing promos below)
function isPromoPlaced(ul: HTMLElement, position: number): boolean {
  return !!ul.querySelector(`li.grid__item__promo[data-order="${position}"], li[data-small-promo-position="${position}"]`);
}

// Products to request so the batch (products + the promos it will receive) fills exactly
// nb_products cells, which keeps the last row complete on every page
function getBatchSize(section: HTMLElement, startOrder: number, fallback: string): number {
  const ul = section.querySelector('ul') as HTMLElement | null;
  const total = Number(ul?.dataset.pageSize);
  if (!ul || !total) return Number(fallback);
  const repeat = ul.dataset.repeatPromosPerPage === 'true';
  const start = repeat ? 0 : startOrder;
  const promos = Array.from(section.querySelectorAll<HTMLTemplateElement>('template[data-position]'))
    .map(t => ({ position: Number(t.dataset.position), cells: promoCells(t.dataset.size ?? '') }))
    .filter(p => repeat || !isPromoPlaced(ul, p.position));
  // Promos at positions start+1..start+size sit before a product of this batch; the one at
  // start+size+1 closes the batch (trailing), so all of them take cells in this page
  const cellsFor = (size: number): number => promos
    .filter(p => p.position > start && p.position <= start + size + 1)
    .reduce((sum, p) => sum + p.cells, 0);
  let best = 1;
  for (let size = total; size >= 1; size--) {
    const filled = size + cellsFor(size);
    if (filled === total) return size;
    if (filled < total && size > best) best = size;
  }
  return best;
}

async function fetchCollectionProductsByProductType(
  Manager: ProductTypeManager,
  isReset = true,
  cursor: string | null = null,
  signal?: AbortSignal
): Promise<boolean | void> {
  const currentBuild = Manager.sectionType;
  try {
    // A reset always lists the current collection, whatever source was active before
    const isParent = !isReset && Manager.isParentSource();
    // nb_products is cached on the manager to avoid a DOM lookup on every fetch
    const section = document.querySelector(`[data-section="${currentBuild}"]`) as HTMLElement | null;
    if (!section) {
      console.warn(`Section ${currentBuild} not found`);
      return;
    }

    // Last real product's order (promos share their product's order, video promos have none)
    const lastProductItem = isReset ? null : section.querySelector('ul')?.querySelectorAll('li.grid__item[data-order]:not(.grid__item__promo)');
    const lastProduct = lastProductItem && lastProductItem.length ? (lastProductItem[lastProductItem.length - 1] as HTMLElement) : null;
    const startOrder = lastProduct ? Number(lastProduct.dataset.order) : 0;
    const pageSize = isParent ? Manager.nbProducts : String(getBatchSize(section, startOrder, Manager.nbProducts));
    const query = buildTypeQuery(currentBuild, pageSize, cursor, isParent ? { handle: parentCollection!.handle } : {});

    const loadingState = section.querySelector('.load-more-row')!;
    loadingState.querySelector('.loader')!.classList.remove('--hidden');

    const response = await fetch(cfState.shopUrl + '/api/2026-04/graphql.json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': cfState.accessToken
      },
      body: JSON.stringify({ query }),
      signal
    });

    if (!response.ok) throw new Error('Network response was not ok');
    const data: GraphQLProductsResponse = await response.json();
    const products = data.data?.collection?.products;

    if (isReset) delete section.dataset.listSource;

    if (isParent && !products) {
      // Parent collection unavailable (e.g. not published on this market): stop, never remove the row
      section.dataset.hasNextPage = 'false';
      loadingState.classList.add('--hidden');
      return;
    }

    if (products && isParent) {
      // The Storefront API can't filter "not in collection X" — drop the current collection's products here
      const excluded = await parentCollection!.getExcludedIds();
      // A reset may have fired during the await: this page belongs to the old filters
      if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
      products.edges = products.edges.filter(({ node }) => !excluded.has(node.id));
      if (products.edges.length === 0) {
        // A page made only of excluded products: jump past the whole excluded run with
        // id-only pages instead of paging through it at full size
        const next = products.pageInfo.hasNextPage
          ? await parentCollection!.findNextStart(currentBuild, products.pageInfo.endCursor)
          : null;
        if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
        if (next) return fetchCollectionProductsByProductType(Manager, false, next.cursor, signal);
        section.dataset.hasNextPage = 'false';
        loadingState.classList.add('--hidden');
        return;
      }
    }

    if (products) {
      if (products.edges.length > 0) {
        const ulElement = section.querySelector('ul') as HTMLElement | null;
        if (!ulElement) {
          if (loadingState) loadingState.classList.add('--hidden');
          return false;
        }

        const currentOrder = startOrder;

        const previousGridHeight = Math.ceil(ulElement.getBoundingClientRect().height);
        if (isReset && previousGridHeight > 0) {
          ulElement.style.minHeight = `${previousGridHeight}px`;
        }

        // Build a position→templates map once so the per-product loop does a
        // Map lookup instead of a querySelectorAll call for every product.
        // Promos belong to the current collection's listing — never interleave them with parent products
        const repeatPromosPerPageEarly = ulElement.dataset.repeatPromosPerPage === 'true';
        const promoTemplateMap = new Map<string, HTMLTemplateElement[]>();
        if (!isParent) section.querySelectorAll('template[data-position]').forEach(t => {
          const pos = (t as HTMLTemplateElement).dataset.position!;
          if (!repeatPromosPerPageEarly && !isReset && isPromoPlaced(ulElement, Number(pos))) return;
          if (!promoTemplateMap.has(pos)) promoTemplateMap.set(pos, []);
          promoTemplateMap.get(pos)!.push(t as HTMLTemplateElement);
        });

        // Accumulate HTML parts in an array then join once — avoids N intermediate
        // string allocations from repeated += on large strings.
        const htmlParts: string[] = [];
        const repeatPromosPerPage = ulElement.dataset.repeatPromosPerPage === 'true';

        products.edges.forEach(({ node: product }, i) => {
          const order = currentOrder + i + 1;
          const promoPosition = repeatPromosPerPage ? (i + 1) : order;

          const promoToPlace = promoTemplateMap.get(String(promoPosition)) ?? [];

          if (promoToPlace.length > 0) {
            promoToPlace.forEach((promo) => {
              let promoHtml = promo.innerHTML;

              if (repeatPromosPerPage) {
                promoHtml = promoHtml
                  .replace(/style="order: [^;]+;/, `style="order: ${order};`)
                  .replace(/data-order="[^"]+"/, `data-order="${order}"`);
              }

              htmlParts.push(promoHtml);
            });
          }

          htmlParts.push(buildProductHtml(product, order));
        });

        const lastIndexInBatch = products.edges.length;
        const nextOrder = Number(currentOrder) + lastIndexInBatch + 1;
        const nextPromoPosition = repeatPromosPerPage ? (lastIndexInBatch + 1) : nextOrder;

        // Closes the batch; the next batch skips it (isPromoPlaced) so it isn't rendered twice
        const trailingPromos = promoTemplateMap.get(String(nextPromoPosition)) ?? [];

        if (trailingPromos.length > 0) {
          trailingPromos.forEach((promo) => {
            let promoHtml = promo.innerHTML;
            if (repeatPromosPerPage) {
              promoHtml = promoHtml
                .replace(/style="order: [^;]+;/, `style="order: ${nextOrder};`)
                .replace(/data-order="[^"]+"/, `data-order="${nextOrder}"`);
            }
            htmlParts.push(promoHtml);
          });
        }

        const productHTML = htmlParts.join('');

        if (isReset) {
          ulElement.innerHTML = productHTML;
          requestAnimationFrame(() => {
            ulElement.style.minHeight = '';
          });
        } else {
          ulElement.innerHTML += productHTML;
        }
        reinitWishlistWidget();

        section.removeAttribute('hidden');
        if (products.pageInfo.hasNextPage) {
          section.dataset.cursor = products.pageInfo.endCursor;
          section.dataset.hasNextPage = 'true';
          loadingState.classList.remove('--hidden');
          loadingState.querySelector('.loader')!.classList.add('--hidden');
          (loadingState.querySelector('button') as HTMLButtonElement).classList.remove('--hidden');
        } else {
          section.dataset.hasNextPage = 'false';
          loadingState.classList.add('--hidden');
        }

        const typeButton = document.querySelector(`[data-target="${Manager.sectionType}"]`);
        if (typeButton && typeButton.parentElement) {
          typeButton.parentElement.removeAttribute('hidden');
        }
      } else {
        if (Manager.sectionType != "fcu") {
          const currentList = section.querySelector('ul')!;
          if (document.documentElement.lang.toUpperCase() == 'FR') {
            currentList.innerHTML = "<li class='empty'>Aucun produit avec le type d'appareil sélectionné</li>";
          } else {
            currentList.innerHTML = "<li class='empty'>No product with the selected device type</li>";
          }
        } else {
          section.remove();
          const typeButton = document.querySelector(`[data-target="${Manager.sectionType}"]`);
          if (typeButton && typeButton.parentElement) {
            typeButton.parentElement.setAttribute('hidden', 'hidden');
          }
          Manager.isValid = false;
        }

        if (loadingState) loadingState.classList.add('--hidden');
      }
    } else {
      if (loadingState) loadingState.classList.add('--hidden');

      section.remove();
      const typeButton = document.querySelector(`[data-target="${Manager.sectionType}"]`);
      if (typeButton && typeButton.parentElement) {
        typeButton.parentElement.setAttribute('hidden', 'hidden');
      }
      Manager.isValid = false;
    }

    const filtersLoader = document.querySelector('.filters-container .filters__loader');
    if (filtersLoader) {
      filtersLoader.classList.remove('--open');
    }
    return;
  } catch (error) {
    // AbortError is expected when a reset cancels an in-flight request — not a real error
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    console.error('Error fetching products:', error);
    const loadingState = document.querySelector(`[data-section="${currentBuild}"] .load-more-row`);
    if (loadingState) loadingState.classList.add('--hidden');
    const filtersLoader = document.querySelector('.filters-container .filters__loader');
    if (filtersLoader) {
      filtersLoader.classList.remove('--open');
    }
    return false;
  }
}

class ProductTypeManager {
  section: HTMLElement | null;
  filterManager!: FilterManager;
  lazyLoader: LazyLoader | null = null;
  isValid = true;
  isLoading = false;
  sectionType!: string;
  // Cached at construction to avoid a DOM query on every fetch call
  nbProducts!: string;
  // Resolves once the first batch of products is rendered (or restored)
  initialLoad: Promise<void> = Promise.resolve();
  // Bumped on every reset so a pending parent-offer check knows its result is stale
  parentGeneration = 0;
  private needsFullSave = false;
  private _abortController: AbortController | null = null;

  constructor(sectionType: string, filterManager: FilterManager) {
    this.section = document.querySelector(`[data-section="${sectionType}"]`);
    if (!this.section) return;
    this.filterManager = filterManager;
    this.sectionType = sectionType;
    this.nbProducts = (this.section.querySelector('ul') as HTMLElement | null)?.dataset.ogn ?? '12';

    const ul = this.section.querySelector('ul') as HTMLElement | null;
    const wasRestored = ul && ul.dataset.restored === 'true';

    const setupAfterLoad = (): void => {
      if (this.isValid) {
        this.filterManager.showProductTypeFilter(this.sectionType);
        const loadMoreButton = this.section!.querySelector(cfSelectors.loadMoreButton);
        if (this.section!.dataset.hasNextPage) {
          loadMoreButton?.addEventListener('click', e => {
            e.preventDefault();
            this.filterManager.setActiveSection(this.sectionType);
            this.lazyLoader = new LazyLoader(this);
          });
        }
      }
      this.isLoading = false;
      this.requestParentCardIfExhausted();
    };

    parentCollection?.bindSection(this);

    if (wasRestored) {
      setupAfterLoad();
    } else {
      this.initialLoad = fetchCollectionProductsByProductType(this, true).then((result) => {
        if (result !== false) this.markLoaded();
        setupAfterLoad();
        saveCollectionState(this.sectionType);
      });
    }
  }

  // Lets saveCollectionState persist this list: until a fetch succeeds it only holds skeletons
  private markLoaded(): void {
    const ul = this.section?.querySelector('ul') as HTMLElement | null;
    if (ul) ul.dataset.loaded = 'true';
  }

  // The list source lives on the section element so back-nav restore carries it
  isParentSource(): boolean {
    return this.section?.dataset.listSource === 'parent';
  }

  activateParentSource(startCursor: string | null): void {
    if (!this.section) return;
    this.section.dataset.listSource = 'parent';
    this.section.dataset.hasNextPage = 'true';
    // Starts past the parent's leading products that this collection already lists
    if (startCursor) this.section.dataset.cursor = startCursor;
    else delete this.section.dataset.cursor;
    // The snapshot's list source and cursor just changed: rewrite the whole list on the next save
    this.needsFullSave = true;
    // Same as the Load More click: infinite scroll takes over after this first batch
    this.filterManager.setActiveSection(this.sectionType);
    this.lazyLoader = new LazyLoader(this);
    this.loadMoreContent();
  }

  private requestParentCardIfExhausted(): void {
    if (!parentCollection || !this.isValid || this.isParentSource()) return;
    if (this.section?.dataset.hasNextPage !== 'false') return;
    parentCollection.requestOffer(this);
  }

  loadMoreContent(isReset = false): void {
    if (!this.section?.dataset.hasNextPage && !isReset) return;
    // Allow a reset to interrupt an in-flight load; block stacked load-more requests
    if (this.isLoading && !isReset) return;

    if (isReset) {
      // Cancel any in-flight request so stale data doesn't overwrite fresh results
      this._abortController?.abort();
      // A filter/device change always restarts from the current collection
      if (this.section) delete this.section.dataset.listSource;
      parentCollection?.clearOffer(this);
      this.parentGeneration++;
    }
    this._abortController = new AbortController();
    this.isLoading = true;
    const cursor = isReset ? null : (this.section?.dataset.cursor ?? null);

    fetchCollectionProductsByProductType(this, isReset, cursor, this._abortController.signal)
      .then((result) => {
        if (result !== false) this.markLoaded();
        if (!isReset) {
          const ul = this.section?.querySelector('ul') as HTMLElement | null;
          if (ul) ul.dataset.timeReload = String(parseInt(ul.dataset.timeReload ?? '1') + 1);
        }

        if (this.section?.dataset.hasNextPage == 'false' || !this.section?.dataset.hasNextPage) {
          this.lazyLoader?.disconnect();
          this.lazyLoader = null;
        }
        this.isLoading = false;
        (this.lazyLoader as LazyLoader | null)?.recheck();
        const fullSave = isReset || this.needsFullSave;
        this.needsFullSave = false;
        saveCollectionState(fullSave ? this.sectionType : undefined);
        this.requestParentCardIfExhausted();
      })
      .catch((err) => {
        if (!(err instanceof DOMException && err.name === 'AbortError')) {
          console.error('ProductTypeManager load error:', err);
        }
        this.isLoading = false;
      });
  }
}

class LazyLoader {
  activeManager: ProductTypeManagerLike;
  observer: IntersectionObserver;
  private sentinel: Element | null;

  constructor(manager: ProductTypeManagerLike) {
    this.activeManager = manager;
    // Watches the Load More row itself with a look-ahead. The old observer used a -75% root
    // margin (negative-size root) and only fired on transitions, which Safari doesn't honor.
    this.observer = new IntersectionObserver(
      (entries) => this.handleIntersection(entries),
      { rootMargin: '0px 0px 600px 0px' }
    );
    this.sentinel = manager.section?.querySelector('.load-more-row') ?? null;
    this.observe(this.sentinel);
    // Activation (Load More click, type tab) loads the next batch right away, then scrolling takes over
    setTimeout(() => {
      if (manager.section?.dataset.hasNextPage === 'true' && !manager.isLoading) manager.loadMoreContent();
    }, 0);
  }

  observe(element: Element | null): void {
    if (element) {
      this.observer.observe(element);
    }
  }

  // An observer only reports changes: after a batch lands with the row still in range,
  // re-observing forces a fresh report so the next batch loads
  recheck(): void {
    if (!this.sentinel) return;
    this.observer.unobserve(this.sentinel);
    this.observer.observe(this.sentinel);
  }

  disconnect(): void {
    this.observer?.disconnect();
  }

  handleIntersection(entries: IntersectionObserverEntry[]): void {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      if (this.activeManager.section?.dataset.hasNextPage !== 'true') return;
      if (!this.activeManager.isLoading) this.activeManager.loadMoreContent();
    });
  }
}

// Initialization
document.addEventListener('DOMContentLoaded', async () => {
  if (isSafari(15.6)) {
    document.querySelector(cfSelectors.mainContent)?.classList.add('--safari');
  }

  // Restore (awaited) before the managers are built so they see ul[data-restored] and skip
  // their initial fetch. Restoring on pageshow was too late: the managers' first-page fetch
  // could resolve after it and wipe the restored list, dropping the user into the footer.
  // bfcache restores (pageshow.persisted) keep the live DOM, so they need nothing.
  const navEntries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
  const wasRestored = navEntries[0]?.type === 'back_forward' && await restoreCollectionState();

  const filterManager = new FilterManager();
  const ImpactManager = document.querySelector('[data-section="tc"]') ? new ProductTypeManager('tc', filterManager) : null;
  const CorpoPhoneCaseManager = document.querySelector('[data-section="cpc"]') ? new ProductTypeManager('cpc', filterManager) : null;
  const FolioManager = document.querySelector('[data-section="fc"]') ? new ProductTypeManager('fc', filterManager) : null;
  const FolioUnisManager = document.querySelector('[data-section="fcu"]') ? new ProductTypeManager('fcu', filterManager) : null;
  const ClearCaseManager = document.querySelector('[data-section="cc"]') ? new ProductTypeManager('cc', filterManager) : null;
  const AirPodsManager = document.querySelector('[data-section="ac"]') ? new ProductTypeManager('ac', filterManager) : null;
  const IPadManager = document.querySelector('[data-section="ic"]') ? new ProductTypeManager('ic', filterManager) : null;
  const IPadClearManager = document.querySelector('[data-section="icc"]') ? new ProductTypeManager('icc', filterManager) : null;
  const SkinManager = document.querySelector('[data-section="ms"]') ? new ProductTypeManager('ms', filterManager) : null;
  const AccessoriesManager = document.querySelector('[data-section="a"]') ? new ProductTypeManager('a', filterManager) : null;
  const ChargePodsManager = document.querySelector('[data-section="cp"]') ? new ProductTypeManager('cp', filterManager) : null;
  const KindleManager = document.querySelector('[data-section="kc"]') ? new ProductTypeManager('kc', filterManager) : null;

  parentCollection?.start(
    [ImpactManager, CorpoPhoneCaseManager, FolioManager, FolioUnisManager, ClearCaseManager, AirPodsManager, IPadManager, IPadClearManager, SkinManager, AccessoriesManager, ChargePodsManager, KindleManager]
      .map((manager) => manager?.initialLoad ?? Promise.resolve())
  );

  filterManager.setManagers(ImpactManager, CorpoPhoneCaseManager, FolioManager, FolioUnisManager, ClearCaseManager, AirPodsManager, IPadManager, IPadClearManager, SkinManager, AccessoriesManager, ChargePodsManager, KindleManager);

  initializeNewFiltersIntegration(filterManager, wasRestored);
  if (wasRestored) restoreViewState(filterManager);

  // pagehide fires on every way out (product card, menu, swipe back) and allows a synchronous write
  window.addEventListener('pagehide', () => saveViewState(getCollectionStateKey(), filterManager.currentActiveSection, cfSelectors.sections));
  initializeCollectionsFilterHydration();
});

export {};
